import { Injectable } from '@angular/core';
import { map, Observable, of } from 'rxjs';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { catchError } from 'rxjs/operators';
import { IProductPacking } from '../../product/models/product-card';
import { Utils } from './utils.service';
import { ProductGroup } from '../../planogram/models/product-group';
import { HttpService } from './http.service';
import { HttpUrlEncodingCodec, NOT_CANCELLED_HEADER } from '../interceptors/auth.interceptor';
import { ISyncInfo, Sync } from '../../product/modals/update-product-modal/update-product-modal.component';
import { TableDataRow } from '../../product/pages/assortment-upload/assortment-upload.component';
import {
	AssortmentType,
	IAssortmentType,
	ICompressionParams,
	IProductCard,
	IProductCardParams,
	IProductConf,
	IProductGeneralInfo,
	IProductHistoryChild,
	IProductHistoryTableItem,
	IUpdatedParams,
	MPProductsPayload
} from '../../product/models/models';
import { ITableData } from '../models/table';
import { SocketGroup, SocketService } from './socket.service';
import { SessionService } from './session.service';
import { MixpanelService } from './mixpanel.service';
import { MixpanelEvents } from '../models/mixpanelEvents';
import { IProductListItem } from '../../product/models/productListItem';
import { FormControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { ProductParams } from '../../planogram/models/product';
import { IActiveProduct } from '../../dashboard/models/activeProduct';
import { IActiveProductsChildrenData } from '../../dashboard/models/activeProductsModal';
import { IInactiveProduct } from '../../dashboard/models/inactiveProduct';
import { IInactiveProductsChildrenData } from '../../dashboard/models/inactiveProductsModal';
import { IProductHoles } from '../../dashboard/models/productHoles';
import { IProductHolesChildrenData } from '../../dashboard/models/productHolesModal';
import { IUnLaidProduct } from '../../dashboard/models/unLaidProduct';
import { ServiceFeatures } from '../models/serviceFeatures';
import { DwUsersActivityService } from './dw-users-activity.service';
import { DwUserActivityNames } from '../models/dwUserActivityNames';
import { DwUserActivityTypes } from '../models/dwUsersActivityTypes';
import { IMasterProductsTableItem } from '../../product/models/masterProductsTableItem';
import {
	IArchiveProductsModalResult
} from '../../product/modals/products-archive-modal/products-archive-modal.component';

export const ROOT_URL: string = '/api/v1/master-products/',
	NEW_ROOT_URL: string = '/api/v1/products/';

export let EMPTY_PRODUCT_IMAGE_PATH: string = '/static/img/img404.jpg';

@Injectable({
	providedIn: 'root'
})
export class ProductsService {
	private _assortmentType: AssortmentType = AssortmentType.NONE;
	listLoadParams: string;
	tasksProductsWorkspaceLoadParams: string;
	tasksUnLaidProductsWorkspaceLoadParams: string;

	static coefficientValidator(): ValidatorFn {
		return (control: FormControl<number>): ValidationErrors | null => {
			return (control.value == 0 || control.value >= 0.01 && control.value <= 0.9)
				? null
				: { coefficientError: true };
		}
	}

	set assortmentType(value: AssortmentType) {
		this._assortmentType = value;
	}

	get isEmptyAssortment(): boolean {
		return this.sessionService.hasFeature(ServiceFeatures.LoadAssortment) && this._assortmentType === AssortmentType.NONE;
	}

	constructor(
		private http: HttpClient,
		private httpService: HttpService,
		private translateService: TranslateService,
		private socketService: SocketService,
		private sessionService: SessionService,
		private mixPanelService: MixpanelService,
		private dwUsersActivityService: DwUsersActivityService
	) {
		EMPTY_PRODUCT_IMAGE_PATH = `/static/img/${this.translateService.currentLang}/img404.jpg`;
		this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
			EMPTY_PRODUCT_IMAGE_PATH = `/static/img/${event.lang}/img404.jpg`;
		});

		if (this.sessionService.hasFeature(ServiceFeatures.LoadAssortment)) {
			this._assortmentType = this.sessionService.defaults.demo.assortment_type;
			this.socketService.addSubscription(SocketGroup.DemoAssortmentType);
			this.socketService.assortmentType.subscribe((response: IAssortmentType) => {
				this._assortmentType = response.assortment_type;
			});
		}
	}

	archive(params: any, data: IArchiveProductsModalResult): Observable<void> {
		let httpParams: HttpParams = new HttpParams({ fromObject: params });
		httpParams = httpParams.set('is_master_position', false);
		return this.http.post<void>(ROOT_URL + 'archive-products/', data, { params: httpParams });
	}

	unarchive(params: any, data: IArchiveProductsModalResult): Observable<void> {
		let httpParams: HttpParams = new HttpParams({ fromObject: params });
		return this.http.post<void>(ROOT_URL + 'unarchive-products/', data, { params: httpParams });
	}

	bulkCreate(products: Array<TableDataRow>): Observable<void> {
		products.forEach((product: TableDataRow) => delete product.id);
		return this.http.post<void>(ROOT_URL + 'bulk-create/', products);
	}

	bulkUpdate(data: Array<IUpdatedParams>): Observable<void> {
		return this.http.post<void>(ROOT_URL + 'bulk-update/', data);
	}

	checkArticleUnique(value: string, params: any = {}): Observable<{ exists: boolean }> {
		const httpParams: HttpParams = new HttpParams({ fromObject: params });
		return this.http.post<{ exists: boolean }>(
			ROOT_URL + 'check-article/',
			{ article: value },
			{ params: httpParams }
		);
	}

	createMasterPosition(params: any): Observable<IProductCard> {
		const httpParams: HttpParams = new HttpParams({
			fromObject: { type: 'master_position', ...params }
		});
		return this.http.post<IProductCard>(ROOT_URL, {}, { params: httpParams });
	}

	createPacking(productId: number, params: any): Observable<void> {
		return this.http.post<void>(ROOT_URL + `${productId}/packings/`, params);
	}

	delete(params: any = {}): Observable<{ ids: Array<number> }> {
		return this.http.delete<{ ids: Array<number> }>(ROOT_URL, {
            headers: new HttpHeaders().set(NOT_CANCELLED_HEADER, 'true'),
			params: new HttpParams({
				fromObject: params
			})
		});
	}

	deletePacking(productId: number, packing: number): Observable<void> {
		return this.http.delete<void>(ROOT_URL + `${productId}/packings/${packing}/`);
	}

	deleteDemoAssortment(): Observable<void> {
		return this.http.delete<void>(ROOT_URL + `delete-demo-assortment/`, { headers: new HttpHeaders().set(NOT_CANCELLED_HEADER, 'true') });
	}

	select(
		values: Array<string>,
		fields: Array<keyof ProductParams> = ['article', 'barcode'],
		params?: any
	): Observable<Array<ProductParams>> {
		const httpParams: HttpParams = new HttpParams({ fromObject: params });
		return this.http.post<Array<ProductParams>>(ROOT_URL + 'select/', { values, fields }, { params: httpParams });
	}

	exist(articles: Array<string>): Promise<Array<ProductParams>> {
		return this.httpService.getPromise(
			this.http.post(ROOT_URL + 'select/', { values: articles, fields: ['article'] })
		);
	}

	generateArticle(): Observable<string> {
		return this.http
			.get<{ article: string }>(ROOT_URL + 'generate-article/')
			.pipe(map((response: { article: string }) => response.article));
	}

	getLayoutRulesProducts(planId, data: any): Promise<any> {
		return this.httpService.getPromise(
			this.http.post(ROOT_URL + `layout-rule-products/?planogram_id=${planId}`, { product_filters: data })
		);
	}

	getActiveProducts(params?: any): Observable<ITableData<IActiveProduct>> {
		return this.http.get<ITableData<IActiveProduct>>('/api/v1/reports/active-products/', {
			params: new HttpParams({
				fromObject: params
			})
		});
	}

	getActiveProductsModal(key: string, params?: any): Observable<IActiveProductsChildrenData> {
		let httpParams: HttpParams = new HttpParams({
			fromObject: params
		});

		httpParams = httpParams.set('key', key);

		return this.http.get<IActiveProductsChildrenData>(`/api/v1/reports/active-products/`, {
			params: httpParams
		});
	}

	getInactiveProducts(params?: any): Observable<ITableData<IInactiveProduct>> {
		return this.http.get<ITableData<IInactiveProduct>>('/api/v1/reports/inactive-products/', {
			params: new HttpParams({
				fromObject: params
			})
		});
	}

	getInactiveProductsModal(key: string, params?: any): Observable<IInactiveProductsChildrenData> {
		let httpParams: HttpParams = new HttpParams({
			fromObject: params
		});

		httpParams = httpParams.set('key', key);

		return this.http.get<IInactiveProductsChildrenData>(`/api/v1/reports/inactive-products/`, {
			params: httpParams
		});
	}

    getLaidPlanogramsCount(params: any): Observable<{ amount: number }> {
        const httpParams: HttpParams = new HttpParams({ fromObject: params });
        return this.http.get<{ amount: number }>(ROOT_URL + 'laid-planograms-amount/', { params: httpParams });
    }

	getMasterPositionCount(params: any): Observable<{ count: number }> {
		const httpParams: HttpParams = new HttpParams({
			fromObject: { is_master_position: true, ...params }
		});
		return this.http.get<{ count: number }>(ROOT_URL + 'count/', { params: httpParams });
	}

	getProductGeneralInfo(id: number): Observable<IProductGeneralInfo> {
		return this.http.get<IProductGeneralInfo>(ROOT_URL + `${id}/short-info/`);
	}

	getMasterProductsTable(id: number, params: any = {}): Observable<ITableData<IMasterProductsTableItem>> {
		const httpParams: HttpParams = new HttpParams({ fromObject: params });
		return this.http.get<ITableData<IMasterProductsTableItem>>(`${ROOT_URL}${id}/products-table/`, { params: httpParams });
	}

	getProductCard(id: number, params?: IProductCardParams): Observable<IProductCard> {
		return this.http.get<IProductCard>(ROOT_URL + `${id}/`, {
			params: new HttpParams({
				fromObject: (params as any) || {}
			})
		});
	}

	getProductHistory(id: number, params: any = {}): Observable<ITableData<IProductHistoryTableItem>> {
		return this.http.get<ITableData<IProductHistoryTableItem>>(ROOT_URL + `${id}/history/`, {
			params: new HttpParams({
				fromObject: params,
				encoder: new HttpUrlEncodingCodec()
			})
		});
	}

	getProductHistoryChildren(historyId: number, productId: number): Observable<IProductHistoryChild> {
		return this.http.get<IProductHistoryChild>(ROOT_URL + `${productId}/history/${historyId}/`);
	}

	getProductHoles(params?: any): Observable<ITableData<IProductHoles>> {
		return this.http.get<ITableData<IProductHoles>>('/api/v1/reports/product-holes/', {
			params: new HttpParams({
				fromObject: params
			})
		});
	}

	getProductHolesModal(key: string, params?: any): Observable<IProductHolesChildrenData> {
		let httpParams: HttpParams = new HttpParams({
			fromObject: params
		});

		httpParams = httpParams.set('key', key);

		return this.http.get<IProductHolesChildrenData>(`/api/v1/reports/product-holes/`, {
			params: httpParams
		});
	}

	getProductHolesPrediction(params?: any): Observable<ITableData<IProductHoles>> {
		return this.http.get<ITableData<IProductHoles>>('/api/v1/reports/product-holes-prediction/', {
			params: new HttpParams({
				fromObject: params
			})
		});
	}

	getProductHolesPredictionModal(key: string, params?: any): Observable<IProductHolesChildrenData> {
		let httpParams: HttpParams = new HttpParams({
			fromObject: params
		});

		httpParams = httpParams.set('key', key);

		return this.http.get<IProductHolesChildrenData>(`/api/v1/reports/product-holes-prediction/`, {
			params: httpParams
		});
	}

	getProductsTable(params: any = {}): Observable<ITableData<IMasterProductsTableItem>> {
		return this.http.get<ITableData<IMasterProductsTableItem>>(
			'api/v1/products-table/',
			{ params: new HttpParams({ fromObject: params }) }
		);
	}

	getProductUsage(id: number, params: any = {}): Observable<ITableData<any>> {
		return this.http.get<ITableData<any>>(ROOT_URL + `${id}/usage/`, {
			params: new HttpParams({
				fromObject: params,
				encoder: new HttpUrlEncodingCodec()
			})
		});
	}

	getProductUsageChildren(id: number, key: string, params?: any): Observable<any> {
		return this.http.get<any>(ROOT_URL + `${id}/usage/?key=${key}`, {
			params: new HttpParams({
				fromObject: params,
				encoder: new HttpUrlEncodingCodec()
			})
		});
	}

	async getProductsByIds(productIds: Array<number>, params: any = {}): Promise<Array<any>> {
		if (!productIds.length) return [];

		const products = productIds.reduce((resultArray, item, index) => {
				const chunkIndex = Math.floor(index / 1e3);

				if (!resultArray[chunkIndex]) {
					resultArray[chunkIndex] = []; // start a new chunk
				}

				resultArray[chunkIndex].push(item);

				return resultArray;
			}, []),

			result = await Promise.all(products.map(p =>
					this.httpService.getPromise(
						this.http.post(
							'/api/v2/reports/master-product-list/',
							{ ids: p },
							{ params: new HttpParams({ fromObject: params }) }
						))
				)
			);

		return Array.prototype.concat(...result);
	}

	getProductsCount(): Observable<{ count: number }> {
		return this.http.get<{ count: number }>(ROOT_URL + 'count/');
	}

	getProductsForEfficiencyAnalysis(data: any): Promise<any> {
		return this.httpService.getPromise(
			this.http.post('api/analytics/planogram/products-predict/', data)
		);
	}

	getProductPaginate(params: any): Observable<ITableData<IProductListItem>> {
		return this.http
			.get<ITableData<IProductListItem>>('/api/v1/master-products-table/', {
				params: new HttpParams({
					fromObject: params,
					encoder: new HttpUrlEncodingCodec()
				})
			})
			.pipe(catchError(() => of(null)));
	}

	getProductsPercent(
		params: any,
		productIds: Array<number>,
		planId: number,
		planogramId: number,
		shopIds: Array<number> = []
	): Promise<any> {
		return this.httpService.getPromise(
			this.http.post('api/analytics/products-place-predict/', {
				planogram: planId,
				stand: planogramId,
				products: productIds,
				shops: shopIds,
				date_from: params.date_from || null,
				date_to: params.date_to || null,
				turnover_coef: params.turnover,
				profit_coef: params.profit,
				qty_coef: params.qty,
				receipts_qty_coef: params.receipts_qty
			})
		);
	}

	getProductsStock(productIds: Array<number>, planId: number, params: any = {}): Promise<any> {
		return this.httpService.getPromise(
			this.http.post('api/analytics/stock-qty/', {
				planogram: planId,
				products: productIds,
				...params
			})
		);
	}

	getProductsToConf(planogramId: number, products: Array<ProductGroup>): Array<IProductConf> {
		return products.map((product: ProductGroup) => ({
			stand: planogramId,
			element_id: product.element.id,
			shelf_no: product.element.idx,
			number: product.number,
			qty: product.qty,
			master_product: product.product.id,
			rotation: product.rotation,
			position_x: Utils.round(product.begin.x),
			position_y: Utils.round(product.begin.y),
			position_z: Utils.round(product.begin.z),
			placement_depth: product.placementDepth,
			placement_height: product.placementHeight,
			packing: product.selectPacking,
			height_compression_coef: product.height_compression_coef,
			width_compression_coef: product.width_compression_coef,
			depth_compression_coef: product.depth_compression_coef,
			applied_compressions: product.applied_compressions,
			depend_product: product.dependProduct ? product.dependProduct.product.id : null,
			error_product: product.error,
			weight: product.weight,
			availability: product.availability,
			angle: product.angle
		}));
	}

	getSyncInfo(params: any): Observable<ISyncInfo> {
		return this.http.get<ISyncInfo>(ROOT_URL + 'sync-info/', {
			params: new HttpParams({
				fromObject: params,
				encoder: new HttpUrlEncodingCodec()
			})
		});
	}

	getSync(params: any, filters: any = {}): Observable<Sync> {
		return this.http.post<Sync>(
			ROOT_URL + 'sync-info/',
			params,
			{ params: new HttpParams({ fromObject: filters }) }
		);
	}

	getStandProductsStock(planogramId: number, planId?: number): Promise<any> {
		const params: Array<string> = [];
		if (planogramId) params.push(`planogram_id=${planogramId}`);
		if (planId) params.push(`plan_id=${planId}`);
		return this.httpService.getPromise(
			this.http.get(ROOT_URL + `stock/${params.length ? '?' + params.join('&') : ''}`)
		);
	}

	getUnLaidProducts(params?: any): Observable<ITableData<IUnLaidProduct>> {
		return this.http.get<ITableData<IUnLaidProduct>>('/api/v1/reports/not-laid-products/', {
			params: new HttpParams({
				fromObject: params
			})
		});
	}

	getUnLaidProductsModal(key: string, params?: any): Observable<IActiveProductsChildrenData> {
		let httpParams: HttpParams = new HttpParams({
			fromObject: params
		});

		httpParams = httpParams.set('key', key);

		return this.http.get<IActiveProductsChildrenData>(`/api/v1/reports/not-laid-products/`, {
			params: httpParams
		});
	}

	googleImages(query: string, barcode: string): Promise<any> {
		return this.httpService.getPromise(
			this.http.post(ROOT_URL + 'google-image-search/', {
				query,
				barcode
			})
		);
	}

	loadTestAssortment(isCreate: boolean = false): Observable<void> {
		if (isCreate) {
			this.mixPanelService.trackPage(MixpanelEvents.DemoAssortmentCreate, { type: 'TEST' });
		} else {
			this.mixPanelService.trackPage(MixpanelEvents.DemoAssortmentChange, { type: 'OWN -> TEST' });
		}

		return this.http.post<void>(
            ROOT_URL + 'demo-assortment/', 
            {}, 
            { headers: new HttpHeaders().set(NOT_CANCELLED_HEADER, 'true') }
        );
	}

	revertParams(id: number, product): Observable<IProductCard> {
		this.dwUsersActivityService.track(DwUserActivityNames.Products, DwUserActivityTypes.Revert);

		return this.http.post<IProductCard>(ROOT_URL + `${product}/history/${id}/revert/`, null);
	}

	setCompression(params: Partial<ICompressionParams>, filterParams: any): Observable<void> {
		this.dwUsersActivityService.track(DwUserActivityNames.Products, DwUserActivityTypes.Compression);

		return this.http.post<void>(ROOT_URL + 'compress/', params, {
			params: new HttpParams({
				fromObject: filterParams,
				encoder: new HttpUrlEncodingCodec()
			})
		});
	}

	startSync(params: any, filters: any = {}): Observable<void> {
		this.dwUsersActivityService.track(DwUserActivityNames.Products, DwUserActivityTypes.Update);

		return this.http.post<void>(
			ROOT_URL + 'start-sync/',
			params,
			{ params: new HttpParams({ fromObject: filters })}
		);
	}

	// TODO: need use new method
	update(id: number, data: any) {
		this.dwUsersActivityService.track(DwUserActivityNames.Products, DwUserActivityTypes.Edit);

		return this.httpService.getPromise(
			this.http.patch(ROOT_URL + `${id}/`, data)
		);
	}

	updateMPProducts(id: number, data: MPProductsPayload): Observable<MPProductsPayload> {
		return this.http.put<MPProductsPayload>(`${ROOT_URL}${id}/products/`, data);
	}

	updatePacking(productId: number, packing: IProductPacking): Observable<void> {
		const id: number = packing.id;
		delete packing.id;
		return this.http.patch<void>(ROOT_URL + `${productId}/packings/${id}/`, packing);
	}

	updateProduct(id: number, params: any): Observable<IProductCard> {
		this.dwUsersActivityService.track(DwUserActivityNames.Products, DwUserActivityTypes.Edit);

		return this.http.patch<IProductCard>(ROOT_URL + `${id}/`, params);
	}
}